import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { MemberImportSummary, MemberImportUploaded } from '@cloudsign/shared';
import type { ImportTarget } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { FileDrop } from '../../components/FileDrop';
import { saveBlob } from '../../components/ExportButton';
import { ErrorPanel, errorMessage } from '../../components/ErrorPanel';
import { Alert, Button, Card, CheckboxField, PageHeader, SelectField, Spinner, TableWrap, Th } from '../../components/ui';
import { useGroupFields } from './hooks';

const ACCEPT = [
  'text/csv',
  'application/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const;
const STANDARD_TARGETS = ['ignore', 'fullName', 'firstName', 'lastName', 'email', 'phone', 'role', 'externalId', 'startDate', 'endDate', 'status'] as const;
const STEPS = ['upload', 'map', 'preview', 'confirm'] as const;
type Step = (typeof STEPS)[number];
type MatchBy = 'email' | 'externalId' | 'fullName' | 'none';
type DateFormat = 'DD/MM/YYYY' | 'YYYY-MM-DD' | 'MM/DD/YYYY';
const POLL_MS = 1000;
const FINISHED = ['done', 'failed', 'cancelled'];

function csvCell(v: string): string {
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

export function ImportWizard() {
  const { id: groupId = '' } = useParams();
  const navigate = useNavigate();
  const back = `/manage/groups/${groupId}/members`;
  const fields = useGroupFields(groupId);

  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [uploaded, setUploaded] = useState<MemberImportUploaded | null>(null);
  const [hasHeader, setHasHeader] = useState(true);
  const [mapping, setMapping] = useState<ImportTarget[]>([]);
  const [matchBy, setMatchBy] = useState<MatchBy>('email');
  const [updateExisting, setUpdateExisting] = useState(true);
  const [archiveMissing, setArchiveMissing] = useState(false);
  const [dateFormat, setDateFormat] = useState<DateFormat>('DD/MM/YYYY');
  const [summary, setSummary] = useState<MemberImportSummary | null>(null);

  const upload = useMutation({
    mutationFn: (f: File) => {
      const form = new FormData();
      form.append('groupId', groupId);
      form.append('file', f);
      return api.post('/members/imports', { form, schema: MemberImportUploaded });
    },
    onSuccess: (u) => {
      setUploaded(u);
      setMapping(u.suggestedMapping);
      setStep('map');
    },
  });

  const validate = useMutation({
    mutationFn: () =>
      api.post(`/members/imports/${uploaded?.importId}/validate`, {
        body: { hasHeader, mapping, matchBy, updateExisting, archiveMissing, dateFormat },
        schema: MemberImportSummary,
      }),
    onSuccess: (s) => {
      setSummary(s);
      setStep('preview');
    },
  });

  const commit = useMutation({
    mutationFn: () => api.post(`/members/imports/${uploaded?.importId}/commit`, { schema: MemberImportSummary }),
    onSuccess: (s) => {
      setSummary(s);
      setStep('confirm');
    },
  });

  const cancel = useMutation({
    mutationFn: () => api.delete(`/members/imports/${uploaded?.importId}`),
    onSuccess: () => navigate(back),
  });

  const progress = useQuery({
    queryKey: ['members', 'import', uploaded?.importId],
    queryFn: () => api.get(`/members/imports/${uploaded?.importId}`, { schema: MemberImportSummary }),
    enabled: step === 'confirm' && uploaded !== null,
    refetchInterval: (q) => (q.state.data && FINISHED.includes(q.state.data.status) ? false : POLL_MS),
  });

  const targetLabel = (tg: string) => t(`membersUi.import.target.${tg}`);
  const fieldTargets = (fields.data ?? []).filter((f) => f.status === 'active');

  const downloadErrors = () => {
    const rows = [t('membersUi.import.errors.csvHeader')];
    for (const e of summary?.errors ?? []) {
      rows.push([String(e.row), csvCell(e.column ?? ''), csvCell(t(e.messageKey, e.params))].join(','));
    }
    saveBlob(new Blob([rows.join('\r\n')], { type: 'text/csv' }), 'member-import-problems.csv');
  };

  const current = STEPS.indexOf(step);
  const live = progress.data ?? summary;

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-4 sm:p-6">
      <PageHeader title={t('membersUi.import.title')} description={t('membersUi.import.description')} />

      <ol aria-label={t('membersUi.import.steps')} className="flex flex-wrap gap-2 text-sm">
        {STEPS.map((s, i) => (
          <li
            key={s}
            aria-current={i === current ? 'step' : undefined}
            className={`rounded-full px-3 py-1 ${i === current ? 'bg-brand text-on-brand' : 'bg-slate-100 text-slate-700'}`}
          >
            {i + 1}. {t(`membersUi.import.step.${s}`)}
          </li>
        ))}
      </ol>

      {step === 'upload' ? (
        <Card>
          <div className="space-y-4">
            <FileDrop label={t('membersUi.import.upload.label')} accept={ACCEPT} maxBytes={20 * 1024 * 1024} onChange={(r) => setFile(r?.file ?? null)} disabled={upload.isPending} />
            {upload.error ? <Alert>{errorMessage(upload.error)}</Alert> : null}
            <div className="flex gap-2">
              <Button variant="primary" disabled={!file || upload.isPending} onClick={() => file && upload.mutate(file)}>
                {upload.isPending ? t('membersUi.import.upload.working') : t('membersUi.import.upload.button')}
              </Button>
              <Button onClick={() => navigate(back)}>{t('common.cancel')}</Button>
            </div>
          </div>
        </Card>
      ) : null}

      {step === 'map' && uploaded ? (
        <Card title={t('membersUi.import.file', { name: uploaded.fileName, count: uploaded.rowCount })}>
          <div className="space-y-5">
            <CheckboxField label={t('membersUi.import.hasHeader')} checked={hasHeader} onChange={(e) => setHasHeader(e.target.checked)} />
            <ul className="space-y-3">
              {uploaded.columns.map((col, i) => (
                <li key={`${i}-${col}`} className="rounded-lg border border-slate-200 p-3">
                  <SelectField
                    label={t('membersUi.import.mapTo', { column: col })}
                    value={mapping[i] ?? 'ignore'}
                    onChange={(e) => setMapping((m) => m.map((x, j) => (j === i ? (e.target.value as ImportTarget) : x)))}
                    hint={`${t('membersUi.import.samples')}: ${uploaded.sampleRows
                      .slice(0, 3)
                      .map((r) => r[i] ?? '')
                      .join(' | ')}`}
                  >
                    {STANDARD_TARGETS.map((tg) => (
                      <option key={tg} value={tg}>
                        {targetLabel(tg)}
                      </option>
                    ))}
                    {fieldTargets.map((f) => (
                      <option key={f.id} value={`field:${f.id}`}>
                        {t('membersUi.import.target.field', { label: f.label })}
                      </option>
                    ))}
                  </SelectField>
                </li>
              ))}
            </ul>
            <div className="grid gap-4 sm:grid-cols-2">
              <SelectField label={t('membersUi.import.matchBy')} value={matchBy} onChange={(e) => setMatchBy(e.target.value as MatchBy)}>
                {(['email', 'externalId', 'fullName', 'none'] as const).map((m) => (
                  <option key={m} value={m}>
                    {t(`membersUi.import.matchBy.${m}`)}
                  </option>
                ))}
              </SelectField>
              <SelectField label={t('membersUi.import.dateFormat')} value={dateFormat} onChange={(e) => setDateFormat(e.target.value as DateFormat)}>
                {(['DD/MM/YYYY', 'YYYY-MM-DD', 'MM/DD/YYYY'] as const).map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </SelectField>
            </div>
            <CheckboxField label={t('membersUi.import.updateExisting')} checked={updateExisting} onChange={(e) => setUpdateExisting(e.target.checked)} />
            <CheckboxField label={t('membersUi.import.archiveMissing')} checked={archiveMissing} onChange={(e) => setArchiveMissing(e.target.checked)} />
            {validate.error ? <Alert>{errorMessage(validate.error)}</Alert> : null}
            <div className="flex flex-wrap gap-2">
              <Button variant="primary" disabled={validate.isPending} onClick={() => validate.mutate()}>
                {validate.isPending ? t('membersUi.import.validating') : t('membersUi.import.validate')}
              </Button>
              <Button disabled={cancel.isPending} onClick={() => cancel.mutate()}>
                {t('membersUi.import.cancel')}
              </Button>
            </div>
          </div>
        </Card>
      ) : null}

      {step === 'preview' && summary ? (
        <div className="space-y-4">
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            {(
              [
                ['create', summary.toCreate],
                ['update', summary.toUpdate],
                ['archive', summary.toArchive],
                ['unchanged', summary.unchanged],
                ['errors', summary.errorCount],
              ] as const
            ).map(([k, n]) => (
              <div key={k} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <dt className="text-xs text-slate-600">{t(`membersUi.import.count.${k}`)}</dt>
                <dd className="text-2xl font-semibold" data-testid={`count-${k}`}>
                  {n}
                </dd>
              </div>
            ))}
          </dl>

          <TableWrap caption={t('membersUi.import.preview.table')}>
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <Th>{t('membersUi.import.preview.row')}</Th>
                <Th>{t('membersUi.import.preview.action')}</Th>
                <Th>{t('membersUi.field.fullName')}</Th>
                <Th>{t('membersUi.field.email')}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {summary.preview.map((r) => (
                <tr key={r.row}>
                  <td className="px-4 py-2">{r.row}</td>
                  <td className="px-4 py-2">{t(`membersUi.import.action.${r.action}`)}</td>
                  <td className="px-4 py-2">{r.fullName}</td>
                  <td className="px-4 py-2">{r.email ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </TableWrap>

          {summary.errors.length > 0 ? (
            <Card title={t('membersUi.import.errors.title')} description={t('membersUi.import.errors.hint')}>
              <ul className="space-y-1 text-sm">
                {summary.errors.map((e, i) => (
                  <li key={`${e.row}-${i}`}>
                    <span className="font-medium">{t('membersUi.import.errors.row', { row: e.row })}</span>
                    {e.column ? ` (${e.column})` : ''}: {t(e.messageKey, e.params)}
                  </li>
                ))}
              </ul>
              <div className="mt-3">
                <Button onClick={downloadErrors}>{t('membersUi.import.errors.download')}</Button>
              </div>
            </Card>
          ) : null}

          {summary.toCreate + summary.toUpdate + summary.toArchive === 0 ? <Alert kind="info">{t('membersUi.import.nothing')}</Alert> : null}
          {commit.error ? <Alert>{errorMessage(commit.error)}</Alert> : null}
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setStep('map')}>{t('common.back')}</Button>
            <Button
              variant="primary"
              disabled={commit.isPending || summary.toCreate + summary.toUpdate + summary.toArchive === 0}
              onClick={() => commit.mutate()}
            >
              {t('membersUi.import.confirm')}
            </Button>
            <Button disabled={cancel.isPending} onClick={() => cancel.mutate()}>
              {t('membersUi.import.cancel')}
            </Button>
          </div>
        </div>
      ) : null}

      {step === 'confirm' ? (
        <Card>
          {progress.error ? (
            <ErrorPanel error={progress.error} onRetry={() => void progress.refetch()} />
          ) : !live ? (
            <Spinner />
          ) : (
            <div className="space-y-3" aria-live="polite">
              {live.status === 'done' ? (
                <Alert kind="success">{t('membersUi.import.done')}</Alert>
              ) : live.status === 'failed' ? (
                <Alert>{t('membersUi.import.failed')}</Alert>
              ) : live.status === 'cancelled' ? (
                <Alert kind="info">{t('membersUi.import.cancelled')}</Alert>
              ) : (
                <div role="status" className="text-sm">
                  {t('membersUi.import.progress')}
                </div>
              )}
              <p className="text-sm text-slate-700">
                {t('membersUi.import.count.create')}: {live.toCreate}. {t('membersUi.import.count.update')}: {live.toUpdate}. {t('membersUi.import.count.archive')}: {live.toArchive}.{' '}
                {t('membersUi.import.count.errors')}: {live.errorCount}.
              </p>
              {FINISHED.includes(live.status) ? (
                <Button variant="primary" onClick={() => navigate(back)}>
                  {t('membersUi.import.backToMembers')}
                </Button>
              ) : null}
            </div>
          )}
        </Card>
      ) : null}
    </div>
  );
}
